import _ from 'lodash';

import {
  CheckboxRenderType,
  DefaultTemplateElementPosition,
  DYNAMIC_FIELD_DEFAULT_MULTIPLIER,
  DynamicFieldPostfixSymbol,
  DynamicFieldPrefixSymbol,
  FieldType,
  FormSchemaType,
  HorizontalAlignmentType,
  VerticalAlignmentType,
  NUMERIC_FIELDS_WITH_DECIMAL_POSITIONS,
  NUMERIC_FIELDS_WITH_THOUSANDS_SEPARATOR,
  TAGS_CATEGORY_NAME,
} from 'constants/forms';
import { getConditionalObject } from 'utils/data';
import {
  checkIsTagAssignedDate,
  checkIsTagName,
  getActionProperty,
  getFieldDecimalPositionsDefault,
  getWithThousandsSeparatorFromAction,
} from 'utils/forms';

import {
  BATCH_EXCLUDED_FIELD_TYPES,
  BatchFieldLayout,
  BatchPanelLayout,
} from './BatchAddDynamicFields.constants';

const DECIMAL_POSITION_FIELD_TYPES = [
  FormSchemaType.RATE,
  FormSchemaType.CURRENCY,
  ...NUMERIC_FIELDS_WITH_DECIMAL_POSITIONS,
];

const PERCENT_POSTFIX_FIELD_TYPES = [FormSchemaType.RATE, FormSchemaType.PERCENT];

const checkIsBatchableField = ({ type, title }) =>
  !BATCH_EXCLUDED_FIELD_TYPES.includes(type) &&
  !checkIsTagName(title) &&
  !checkIsTagAssignedDate(title);

export const getBatchableDynamicFieldsSchema = (dynamicFieldsSchema = []) =>
  dynamicFieldsSchema.reduce((acc, category) => {
    if (category.title === TAGS_CATEGORY_NAME) {
      return acc;
    }

    const fields = (category.fields ?? []).filter(checkIsBatchableField);
    const categories = getBatchableDynamicFieldsSchema(category.categories ?? []);

    if (!fields.length && !categories.length) {
      return acc;
    }

    acc.push({ ...category, fields, categories });

    return acc;
  }, []);

const getBatchFieldStyle = ({ isPdfForm, isCheckbox }) => ({
  isBold: false,
  isUppercase: false,
  fontColor: null,
  fontSize: 0,
  fontFamily: 0,
  ...getConditionalObject(isPdfForm, () => ({
    backgroundColor: null,
    ...(isCheckbox
      ? { checkboxRenderType: CheckboxRenderType.Checkbox.value }
      : {
          align: HorizontalAlignmentType.CENTER,
          verticalAlign: VerticalAlignmentType.MIDDLE,
        }),
  })),
});

/**
 * Builds the dynamic field payload that the formatting dialog would save if confirmed without changes.
 */
export const getBatchDynamicField = ({ field, isPdfForm }) => {
  const { value, type, title, label, ignoreUserDefinedStyles, defaultAction } = field;
  const displayTitle = label || title;

  if (ignoreUserDefinedStyles) {
    return { value, type, title: displayTitle, label, style: {} };
  }

  const isCheckbox = type === FormSchemaType.BOOL;

  const action = getActionProperty({
    dataDynamicType: type,
    includeSign: false,
    decimalPositions: DECIMAL_POSITION_FIELD_TYPES.includes(type)
      ? getFieldDecimalPositionsDefault(defaultAction, type)
      : null,
    withThousandsSeparator: NUMERIC_FIELDS_WITH_THOUSANDS_SEPARATOR.includes(type)
      ? getWithThousandsSeparatorFromAction(null, type, defaultAction)
      : null,
    isDateOnly: false,
    checkboxInverseValue: false,
    isSpelledOut: false,
  });

  return {
    value,
    type,
    title: displayTitle,
    label,
    action,
    multiplier: DYNAMIC_FIELD_DEFAULT_MULTIPLIER,
    style: getBatchFieldStyle({ isPdfForm, isCheckbox }),
    ...getConditionalObject(PERCENT_POSTFIX_FIELD_TYPES.includes(type), {
      postfix: DynamicFieldPostfixSymbol.PERCENT,
    }),
    ...getConditionalObject(type === FormSchemaType.CURRENCY, {
      prefix: DynamicFieldPrefixSymbol.DOLLAR,
    }),
    ...getConditionalObject(isCheckbox, { withLabel: !_.isEmpty(label) }),
    ...getConditionalObject(type === FormSchemaType.BLANK_SIGNATURE_LINE, {
      usePoaVerbiage: true,
    }),
  };
};

const checkIsOverlapping = (first, second) =>
  first.x < second.x + second.width &&
  second.x < first.x + first.width &&
  first.y < second.y + second.height &&
  second.y < first.y + first.height;

const getBatchSlots = () => {
  const { START_X, START_Y, ROW_STEP, COLUMN_STEP, ROWS_PER_COLUMN, MAX_COLUMNS } =
    BatchFieldLayout;

  return Array.from({ length: ROWS_PER_COLUMN * MAX_COLUMNS }, (__, index) => ({
    x: START_X + Math.floor(index / ROWS_PER_COLUMN) * COLUMN_STEP,
    y: START_Y + (index % ROWS_PER_COLUMN) * ROW_STEP,
  }));
};

/**
 * Picks the first grid slot that does not overlap an element already on the page,
 * so repeated batch sessions never stack new fields on top of existing ones.
 */
export const getBatchElementPosition = ({ type, pageElements = [] }) => {
  const { height, width: defaultWidth } = DefaultTemplateElementPosition[FieldType.DYNAMIC];
  const isCheckbox = type === FormSchemaType.BOOL;
  const width = isCheckbox ? height : defaultWidth;
  const slots = getBatchSlots();

  const freeSlot = slots.find(slot =>
    pageElements.every(({ position }) => !checkIsOverlapping({ ...slot, width, height }, position)),
  );
  const slot = freeSlot ?? slots[pageElements.length % slots.length];

  return { ...slot, ...getConditionalObject(isCheckbox, { width }) };
};

const clampToViewport = ({ value, size, viewportSize }) => {
  const { VIEWPORT_GUTTER } = BatchPanelLayout;
  const maxValue = viewportSize - size - VIEWPORT_GUTTER;

  return Math.max(VIEWPORT_GUTTER, Math.min(value, maxValue));
};

export const getInitialPanelPosition = ({ anchorRect, viewportWidth, viewportHeight }) => {
  const { WIDTH, ESTIMATED_HEIGHT, ANCHOR_OFFSET } = BatchPanelLayout;

  return {
    x: clampToViewport({
      value: anchorRect.right - WIDTH,
      size: WIDTH,
      viewportSize: viewportWidth,
    }),
    y: clampToViewport({
      value: anchorRect.bottom + ANCHOR_OFFSET,
      size: ESTIMATED_HEIGHT,
      viewportSize: viewportHeight,
    }),
  };
};
